import { FeatureSection } from "./FeatureSection";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Mic, Users, CheckSquare, FileText, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AudioVisualizer } from "@/components/ui/AudioVisualizer";

export const MeetingAnalysisSection = () => {
  const { t } = useTranslation();
  const { ref: visualRef, isVisible: visualVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <FeatureSection id="meeting-analysis" background="default">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: Copy */}
        <div className="space-y-6 text-center lg:text-left max-w-xl mx-auto lg:mx-0 lg:max-w-none">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-medium">
            <Mic className="h-3.5 w-3.5" />
            {t("landing.meetingAnalysis.badge")}
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
            {t("landing.meetingAnalysis.title")}{" "}
            <span className="text-gradient">{t("landing.meetingAnalysis.titleHighlight")}</span>
          </h2>

          <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
            {t("landing.meetingAnalysis.description")}
          </p>

          {/* Features list */}
          <div className="grid sm:grid-cols-2 gap-4 pt-4 justify-items-center lg:justify-items-start">
            <FeatureItem icon={Users} text={t("landing.meetingAnalysis.features.speakerIdentification")} />
            <FeatureItem icon={FileText} text={t("landing.meetingAnalysis.features.smartTranscription")} />
            <FeatureItem icon={CheckSquare} text={t("landing.meetingAnalysis.features.actionItemExtraction")} />
            <FeatureItem icon={Sparkles} text={t("landing.meetingAnalysis.features.aiSummaries")} />
          </div>
        </div>

        {/* Right: Visual */}
        <div
          ref={visualRef}
          className={cn(
            "relative animate-scale-in",
            visualVisible && "is-visible"
          )}
        >
          {/* Main Card */}
          <div className="relative w-full max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/30">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/30 border border-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/30 border border-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/30 border border-green-500/50" />
              </div>
              <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                {t("landing.meetingAnalysis.demo.processing")}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Audio Viz */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs font-medium text-muted-foreground">
                  <span>{t("landing.meetingAnalysis.demo.audioFile")}</span>
                  <span className="text-green-500 animate-pulse">{t("landing.meetingAnalysis.demo.live")}</span>
                </div>
                <div className="h-20 bg-muted/30 rounded-lg border border-border/50 flex items-center justify-center overflow-hidden p-3">
                  <AudioVisualizer />
                </div>
              </div>

              {/* Transcript Lines */}
              <div className="space-y-3">
                <TranscriptLine
                  speaker={t("landing.meetingAnalysis.demo.speakerA")}
                  color="blue"
                  text={t("landing.meetingAnalysis.demo.transcriptA")}
                />
                <TranscriptLine
                  speaker={t("landing.meetingAnalysis.demo.speakerB")}
                  color="purple"
                  text={t("landing.meetingAnalysis.demo.transcriptB")}
                />
              </div>

              {/* Tags */}
              <div className="flex gap-2 pt-2">
                <Tag color="primary">{t("landing.meetingAnalysis.demo.tags.actionItem")}</Tag>
                <Tag color="accent">{t("landing.meetingAnalysis.demo.tags.decision")}</Tag>
                <Tag color="green">{t("landing.meetingAnalysis.demo.tags.deadline")}</Tag>
              </div>
            </div>
          </div>

          {/* Floating elements */}
          <div className="absolute -right-4 top-20 bg-card p-3 rounded-xl shadow-xl border border-border animate-float hidden lg:block">
            <CheckSquare className="w-5 h-5 text-green-500" />
          </div>
          <div className="absolute -left-4 bottom-20 bg-card p-3 rounded-xl shadow-xl border border-border animate-float hidden lg:block" style={{ animationDelay: "1s" }}>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      </div>
    </FeatureSection>
  );
};

const FeatureItem = ({ icon: Icon, text }: { icon: React.ElementType; text: string }) => (
  <div className="flex items-center gap-3 text-sm text-muted-foreground">
    <div className="p-2 rounded-lg bg-primary/10">
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <span>{text}</span>
  </div>
);

const TranscriptLine = ({ speaker, color, text }: { speaker: string; color: string; text: string }) => (
  <div className="flex gap-3 text-sm">
    <span className={cn(
      "font-medium shrink-0",
      color === "blue" && "text-blue-500",
      color === "purple" && "text-purple-500"
    )}>
      {speaker}:
    </span>
    <span className="text-muted-foreground">{text}</span>
  </div>
);

const Tag = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <div className={cn(
    "px-2 py-1 rounded text-[10px] font-mono border",
    color === "primary" && "bg-primary/10 text-primary border-primary/20",
    color === "accent" && "bg-accent/10 text-accent border-accent/20",
    color === "green" && "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
  )}>
    {children}
  </div>
);
